import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { FlexiAdvisorSettingsService } from './flexi-advisor-settings.service';
import {
  FlexiAdvisorAssignment,
  FlexiAdvisorWorkload,
  FlexiBusinessAdvisor,
} from './flexi-advisor-settings.models';
import { initials, move, primaryOf, renumber } from './flexi-advisor-settings.logic';

/**
 * Side panel for one business's advisor list: add, remove and reorder, with a
 * live preview of what the business will see on its General tab.
 *
 * Emits `closed` with true after a successful save and false otherwise. The
 * preview follows the same rule as the backend, which decides for real on save.
 */
@Component({
  selector: 'flexi-advisor-edit-dialog',
  templateUrl: './advisor-edit-dialog.component.html',
  styleUrls: ['./advisor-edit-dialog.component.scss'],
  standalone: false,
})
export class FlexiAdvisorEditDialogComponent implements OnInit, OnDestroy {
  @Input({ required: true }) business!: FlexiAdvisorAssignment;
  /** Active admins of the application: the only advisors that can be added. */
  @Input() selectable: FlexiAdvisorWorkload[] = [];
  @Output() closed = new EventEmitter<boolean>();

  readonly initials = initials;

  visible = true;
  advisors: FlexiBusinessAdvisor[] = [];
  addOptions: SelectItem[] = [];
  primary: FlexiBusinessAdvisor | null = null;
  toAdd: number | null = null;
  saving = false;
  saveFailed = false;

  private original: number[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: FlexiAdvisorSettingsService,
    private notifications: NotificationService,
  ) {}

  /** Copies the saved list so edits and the primary preview remain local until save. */
  ngOnInit(): void {
    this.original = this.business.Advisors.map(advisor => advisor.UserId);
    this.update(renumber(this.business.Advisors.map(advisor => ({ ...advisor }))));
  }

  /** Cancels the pending save when the dialog is destroyed. */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** True when the list differs from the one the dialog opened with. */
  get dirty(): boolean {
    return this.advisors.length !== this.original.length
      || this.advisors.some((advisor, index) => advisor.UserId !== this.original[index]);
  }

  /** Appends a selectable advisor once, preserving the existing priority order. */
  add(): void {
    const candidate = this.selectable.find(advisor => advisor.UserId === this.toAdd);
    if (!candidate || this.advisors.some(advisor => advisor.UserId === candidate.UserId)) {
      return;
    }
    this.update(renumber([
      ...this.advisors,
      {
        UserId: candidate.UserId,
        Name: candidate.Name,
        Email: candidate.Email,
        SortOrder: this.advisors.length,
        IsEligible: true,
        IsPrimary: false,
      },
    ]));
    this.toAdd = null;
  }

  /** Removes the advisor at the given zero-based position and refreshes the preview. */
  remove(index: number): void {
    this.update(renumber(this.advisors.filter((_, position) => position !== index)));
  }

  /** Raises an advisor one position in the priority order. */
  moveUp(index: number): void {
    this.update(move(this.advisors, index, -1));
  }

  /** Lowers an advisor one position in the priority order. */
  moveDown(index: number): void {
    this.update(move(this.advisors, index, 1));
  }

  /** Persists a changed list, closing on success and retaining edits on failure. */
  save(): void {
    if (!this.dirty || this.saving) {
      return;
    }
    this.saving = true;
    this.saveFailed = false;
    this.api
      .setAdvisors(this.business.BusinessId, this.advisors.map(advisor => advisor.UserId))
      .pipe(takeUntil(this.destroy$), finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.notifications.success('FLEXI_SETTINGS.SAVED');
          this.close(true);
        },
        error: () => (this.saveFailed = true),
      });
  }

  /** Discards local edits and reports that no save occurred. */
  cancel(): void {
    this.close(false);
  }

  /** Treats closing the panel through its visibility control as cancellation. */
  visibleChanged(visible: boolean): void {
    if (!visible) {
      this.close(false);
    }
  }

  /** Closes the panel and tells the parent whether assignments need reloading. */
  private close(saved: boolean): void {
    this.visible = false;
    this.closed.emit(saved);
  }

  /** Refreshes the local list, effective primary and remaining selectable advisors. */
  private update(advisors: FlexiBusinessAdvisor[]): void {
    this.advisors = advisors;
    this.primary = primaryOf(advisors);
    this.addOptions = this.selectable
      .filter(candidate => !advisors.some(advisor => advisor.UserId === candidate.UserId))
      .map(candidate => ({ label: candidate.Name ?? '', value: candidate.UserId }));
  }
}
